import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { Modal } from '@/components/ui/modal'
import { useToast } from '@/hooks/useToast'
import { extractApiError } from '@/lib/api'
import { tasksApi } from '@/lib/api/tasks'
import type { BoxCodeRow } from '@/types'

interface BoxContentsModalProps {
  taskUuid: string
  boxNum: number | null
  onClose: () => void
}

export function BoxContentsModal({ taskUuid, boxNum, onClose }: BoxContentsModalProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [codeInput, setCodeInput] = useState('')
  const open = boxNum != null && boxNum > 0

  const query = useQuery({
    queryKey: ['task-box-codes', taskUuid, boxNum],
    queryFn: async () => {
      const res = await tasksApi.getBoxCodes(taskUuid, boxNum!)
      return res.data
    },
    enabled: open,
  })

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['task-box-codes', taskUuid, boxNum] })
    void queryClient.invalidateQueries({ queryKey: ['task-boxes', taskUuid] })
    void queryClient.invalidateQueries({ queryKey: ['task-km-codes', taskUuid] })
    void queryClient.invalidateQueries({ queryKey: ['task', taskUuid] })
  }

  const addMutation = useMutation({
    mutationFn: (code: string) => tasksApi.addBoxCode(taskUuid, boxNum!, code),
    onSuccess: () => {
      setCodeInput('')
      toast.success('Код добавлен')
      invalidate()
    },
    onError: (err) => toast.error(extractApiError(err)),
  })

  const removeMutation = useMutation({
    mutationFn: (canonical: string) => tasksApi.removeBoxCode(taskUuid, boxNum!, canonical),
    onSuccess: () => {
      toast.success('Код удалён')
      invalidate()
    },
    onError: (err) => toast.error(extractApiError(err)),
  })

  const data = query.data
  const codes: BoxCodeRow[] = data?.codes ?? []
  const total = data?.total ?? 0
  const itemsPerBox = data?.items_per_box ?? 0
  const pending = data?.pending_count ?? 0
  const busy = addMutation.isPending || removeMutation.isPending

  const handleAdd = () => {
    const code = codeInput.trim()
    if (!code) {
      toast.error('Введите код')
      return
    }
    addMutation.mutate(code)
  }

  const handleRemove = (row: BoxCodeRow) => {
    if (!confirm(`Удалить код из коробки №${boxNum}?\n${row.canonical}`)) return
    removeMutation.mutate(row.canonical)
  }

  return (
    <Modal isOpen={open} onClose={onClose} className="max-w-2xl">
      <Modal.Header onClose={onClose}>Содержимое коробки №{boxNum}</Modal.Header>
      <Modal.Body className="space-y-4">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>
            КМ в PG: <span className="font-semibold text-gray-900">{total}</span>
            {itemsPerBox > 0 && <> / {itemsPerBox}</>}
            {pending > 0 && (
              <span className="ml-2 text-amber-600">(+{pending} pending)</span>
            )}
          </span>
          {data?.scan_status && (
            <span className="text-xs uppercase tracking-wide text-gray-400">
              {data.scan_status}
            </span>
          )}
        </div>

        <div className="flex gap-2">
          <Input
            value={codeInput}
            onChange={(e) => setCodeInput(e.target.value)}
            placeholder="Вставьте КМ для добавления"
            className="font-mono text-xs"
            disabled={busy}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleAdd()
              }
            }}
          />
          <Button type="button" size="sm" onClick={handleAdd} disabled={busy}>
            Добавить
          </Button>
        </div>

        {query.isLoading ? (
          <div className="flex justify-center py-10">
            <LoadingSpinner />
          </div>
        ) : query.isError ? (
          <p className="text-sm text-red-600 py-6 text-center">{extractApiError(query.error)}</p>
        ) : codes.length === 0 ? (
          <p className="text-sm text-gray-400 py-10 text-center">Коробка пуста</p>
        ) : (
          <ul className="max-h-[50vh] overflow-y-auto divide-y border rounded-lg">
            {codes.map((row) => (
              <li
                key={`${row.source}-${row.canonical}`}
                className="flex items-start gap-2 px-3 py-2 text-sm hover:bg-gray-50"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-mono text-xs break-all text-gray-900">{row.canonical}</p>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    {row.source}
                    {row.status ? ` · ${row.status}` : ''}
                    {row.gtin ? ` · GTIN ${row.gtin}` : ''}
                    {row.sequence_number ? ` · №${row.sequence_number}` : ''}
                  </p>
                </div>
                <button
                  type="button"
                  title="Удалить"
                  disabled={busy}
                  className="p-1.5 rounded text-red-500 hover:bg-red-50 disabled:opacity-40 shrink-0"
                  onClick={() => handleRemove(row)}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="secondary" onClick={onClose}>
          Закрыть
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
